import strings from '../../../strings/strings.json';
import React from 'react';
import TextAreaField from 'components/forms/TextAreaField';
import useModifyRequest from 'hooks/useModifyRequest';
import { createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import useNeveForm from 'components/forms/NeveForm';
import { FormCard } from 'components/forms/FormComponents';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { FlexColLg, FlexLg, FlexMdAlignCenter } from 'components/Elements';
import { useNavigate } from 'react-router-dom';
import { OrganisationOverviewContext } from 'contextProviders/OrganisationOverviewContext';
import UpdateLogo from './UpdateLogo';
import PreviewIcon from 'assets/icons/controls/PreviewIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import Button from 'components/buttons/Button';

type OrganisationDescription = {
    description: string;
};

type OrganisationDescriptionFormProps = {
    savedDescription: boolean;
    setSavedDescription: (value: React.SetStateAction<boolean>) => void;
};

export function OrganisationDescriptionForm({
    savedDescription,
    setSavedDescription,
}: OrganisationDescriptionFormProps): JSX.Element {
    const navigate = useNavigate();
    const { organisation, refetchOrganisation } = React.useContext(OrganisationOverviewContext);

    const [showError, setShowError] = React.useState<boolean>(false);

    const { modifyData: updateDescription, loading: updatingDescription } = useModifyRequest(
        `organisations/${organisation!.id}/description`,
        'PATCH',
    );

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty },
        reset,
    } = useNeveForm<OrganisationDescription>({
        description: organisation!.description ?? '',
    });

    async function onUpdateOrganisationDescription({ description }: OrganisationDescription) {
        setShowError(false);

        const response = await updateDescription({ description: description });

        if (response.errors && response.errors.length > 0) {
            setShowError(true);
        } else {
            setSavedDescription(true);
            reset({ description });
            await refetchOrganisation();
        }
    }

    return (
        <FlexColLg>
            <Notification
                notificationType={NotificationType.INFO}
                title={strings.updateOrganisationForm.viewOrgNotification.title}
                description={strings.updateOrganisationForm.viewOrgNotification.description}
                button={
                    <Button
                        title={strings.updateOrganisationForm.viewOrgButton.title}
                        aria={strings.updateOrganisationForm.viewOrgButton.aria}
                        icon={<PreviewIcon size={IconSize.MEDIUM} />}
                        onClick={() => {
                            navigate(`/public/organisation/${organisation!.publicName}`);
                        }}
                    />
                }
            />

            <OrganisationDetails>
                <OrganisationDescriptionFormCard onSubmit={handleSubmit(onUpdateOrganisationDescription)}>
                    <TextAreaField
                        maxLength={500}
                        charactersLeft={500 - watch('description').length}
                        fieldName={strings.updateOrganisationForm.descriptionInput.title}
                        aria={strings.updateOrganisationForm.descriptionInput.aria}
                        errorMessage={errors.description?.message}
                        {...register('description', {
                            maxLength: {
                                value: 500,
                                message: createErrorMessage(
                                    strings.updateOrganisationForm.descriptionInput.title,
                                    fieldErrorCodes.maxLength,
                                ),
                            },
                        })}
                    />
                    <SaveButtonContainer>
                        {showError && <ErrorNotifierSlim description={strings.updateOrganisationForm.error} />}
                        {savedDescription && !isDirty && (
                            <Notification
                                notificationType={NotificationType.SUCCESS}
                                title={strings.general.saveSuccess}
                            />
                        )}
                        <Button
                            type="submit"
                            title={strings.updateOrganisationForm.submitButton.title}
                            aria={strings.updateOrganisationForm.submitButton.aria}
                            disabled={updatingDescription}
                        />
                    </SaveButtonContainer>
                </OrganisationDescriptionFormCard>

                <Border />

                <UpdateLogo />
            </OrganisationDetails>
        </FlexColLg>
    );
}

const OrganisationDetails = styled(FlexLg)`
    align-items: stretch;

    @media (max-width: ${breakpoints.lg}) {
        flex-direction: column;
        align-items: unset;
        gap: ${sizes.spacingMd};
    }
`;

const OrganisationDescriptionFormCard = styled(FormCard)`
    border: unset;
    box-shadow: unset;
    padding: unset;
`;

const SaveButtonContainer = styled(FlexMdAlignCenter)`
    min-height: 72px;
    justify-content: flex-end;
`;

const Border = styled.hr`
    border: 1px solid ${theme.cardBorder};
`;
